import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ScanLine, Search, Coins, Layers, LibraryBig, Sparkles } from "lucide-react";
import { api, gbp, logoImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CardTile, CardGrid, CardImage } from "@/components/CardTile";
import { CardView } from '@/components/CardBrowser';
import { displayCards } from '@/lib/setCards';
import { ProgressBar, Spinner, Btn, Stat } from "@/components/UI";
import { CollectionValueChart } from "@/components/Charts";

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <Spinner />;
  const s = d.stats;
  const rows = list => list.map(item => ({ ...item, id: item.card_id, owned: item.quantity }));
  return <CardView view="dashboard" controls={false}>{({ display, editButton, editor }) => (
    <div className="space-y-10" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{user?.name}'s collection</p>
          <h1 className="font-display text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">My Collections</h1>
        </div>
        <div className="flex gap-3">
          {editButton}
          <Link to="/scan"><Btn data-testid="dashboard-scan-button"><ScanLine className="h-5 w-5" /> Scan cards</Btn></Link>
          <Link to="/search"><Btn variant="ghost" data-testid="dashboard-search-button"><Search className="h-5 w-5" /> Search</Btn></Link>
        </div>
      </div>
      {editor}
      <div className="grid gap-4 sm:grid-cols-3">{[['/collection', 'Cards', 'Every physical copy you own'], ['/my-sets', 'My Sets', 'Your chosen cards and collecting progress'], ['/storage', 'Binders & Storage', 'Find and arrange your physical cards']].map(([to, title, description]) => <Link key={to} to={to} className="rounded-2xl border bg-white p-5 shadow-sm"><h2 className="font-display text-xl font-black">{title} →</h2><p className="mt-2 text-slate-500">{description}</p></Link>)}</div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4 sm:gap-6">
        <Stat icon={Coins} label="Collection value" value={gbp(s.value_gbp)} testid="stat-value" accent="bg-[#FFCC00]" />
        <Stat icon={LibraryBig} label="Total cards" value={s.total_cards} testid="stat-total-cards" />
        <Stat icon={Sparkles} label="Unique cards" value={s.unique_cards} testid="stat-unique-cards" />
        <Stat icon={Layers} label="Collecting plans" value={d.plan_count ?? s.sets_started} testid="stat-sets-started" />
      </div>

      <section>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="font-display text-2xl font-bold">Collecting progress</h2>
          <Link to="/my-sets" className="text-sm font-bold text-[#E3350D]" data-testid="dashboard-goals-link">My sets →</Link>
        </div>
        {(d.personal_sets || []).length === 0 ? (
          <p className="rounded-2xl bg-white p-6 text-slate-600">No sets tracked yet. Open any set in <Link to="/sets" className="font-bold text-[#E3350D]">Sets</Link> and tap “Track this set”.</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {(d.personal_sets || []).map((g) => (
              <Link key={g.key} to={`/my-sets/${g.kind}/${g.id}`} className="rounded-3xl border border-slate-200/60 bg-white p-5 transition-transform hover:-translate-y-1" data-testid={`dashboard-goal-${g.id}`}>
                <div className="mb-4 flex items-center gap-3">{g.logo ? <img src={logoImg(g.logo)} alt={g.name} className="max-h-12 max-w-[160px] object-contain" /> : <><div className="w-12 shrink-0"><CardImage image={g.cover} name={g.name} /></div><p className="font-display font-bold">{g.name}</p></>}</div>
                <ProgressBar label={`${g.name} · ${g.target}`} value={g.progress[0]} total={g.progress[1]} />
              </Link>
            ))}
          </div>
        )}
      </section>

      {d.top.length > 0 && (
        <section>
          <h2 className="mb-4 font-display text-2xl font-bold">Most valuable</h2>
          <CardGrid testid="dashboard-top-grid">
            {displayCards(rows(d.top), display).map((it, i) => <CardTile key={it.key} index={i} card={it} owned={it.owned} price={it.price_gbp} variant={it.variant} />)}
          </CardGrid>
        </section>
      )}
      <section>
        <h2 className="mb-4 font-display text-2xl font-bold">Recently added</h2>
        {d.recent.length === 0 ? (
          <p className="rounded-2xl bg-white p-6 text-slate-600">Your binder is empty. Try scanning a card or searching for your favourite Pokémon!</p>
        ) : (
          <CardGrid testid="dashboard-recent-grid">
            {displayCards(rows(d.recent), display).map((it, i) => <CardTile key={it.key} index={i} card={it} owned={it.owned} variant={it.variant} />)}
          </CardGrid>
        )}
      </section>
      <CollectionValueChart />
    </div>
  )}</CardView>;
}
